/**
 * Makes the destination and confirmation step explicit for move/copy workflows.
 *
 * Selection starts in one folder but authorization also depends on the chosen
 * destination. This compact boundary shows that target and prevents an item
 * from moving merely because a navigation click occurred.
 */
import { Button, Card, CardContent, Input } from '@/components/ui'
import type { ItemResponse } from '../types'

interface TransferBarProps {
  kind: 'move' | 'copy'
  entry: ItemResponse
  name: string
  canConfirm: boolean
  onNameChange: (name: string) => void
  onCancel: () => void
  onConfirm: () => void
}

export function TransferBar({
  kind,
  entry,
  name,
  canConfirm,
  onNameChange,
  onCancel,
  onConfirm,
}: TransferBarProps) {
  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-3 p-3">
        <div className="min-w-60 flex-1">
          <p className="font-medium">{kind === 'move' ? 'Move' : 'Copy'} {entry.item.name}</p>
          <p className="text-sm text-muted-foreground">
            Browse to the destination. Moving may change inherited collaborators and this confirmation applies that change.
          </p>
        </div>
        {kind === 'copy' && (
          <Input
            className="w-64"
            aria-label="Copy name"
            value={name}
            onChange={(event) => onNameChange(event.target.value)}
          />
        )}
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={onConfirm} disabled={!canConfirm || (kind === 'copy' && !name.trim())}>
          {kind === 'move' ? 'Move here' : 'Copy here'}
        </Button>
      </CardContent>
    </Card>
  )
}
